/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useState, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { Item } from "types/models/menu"
import { SortDrawer } from "components/drawers/SortDrawer"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { MenuItemRow } from "pages/menu/detail/menuOverview/lists/MenuItemRow" // TODO: move component from /pages/
import { sortMenuItemNewLogicWithSortedIndexes } from "utils/sort_helper"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { useMenuV3ItemsInfinite, flattenInfiniteItems } from "queries"
import { FoodmaxapipbEntityType } from "generated"
import { mapItemsV2ToItems } from "utils/menuV3Mapper"

const rowHeight = 121

interface SortItemsDrawerProps {
  visible: boolean
  onClose(): void
}

export const SortItemsDrawer: FC<SortItemsDrawerProps> = ({
  visible,
  onClose,
}: SortItemsDrawerProps) => {
  const dispatch = useDispatch()
  const [sortedIndexes, setSortedIndexes] = useState<[number, number][]>([])

  const {
    merchantID,
    overview: { items: reduxItems, subDepartment, category },
    isMartDepartmentStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    isUCS,
  } = useSelector((state: AppState) => state.menu)

  const enablePartialMenu = useEnablePartialMenu()

  const categoryID = category?.categoryID ?? ""
  const departmentID = subDepartment?.itemClassID ?? ""

  // Determine parentType and parentID based on selected context for partial menu
  const { parentType, parentID } = useMemo(() => {
    if (isMartDepartmentStructure) {
      // Only fetch items when subdepartment is selected (not just department)
      if (departmentID) {
        return {
          parentType: FoodmaxapipbEntityType.SubDepartment,
          parentID: departmentID,
        }
      }
    } else if (categoryID) {
      return {
        parentType: FoodmaxapipbEntityType.Category,
        parentID: categoryID,
      }
    }
    return { parentType: undefined, parentID: undefined }
  }, [isMartDepartmentStructure, categoryID, departmentID])

  // Fetch items from query when partial menu is enabled
  const { currentData: itemsData } = useMenuV3ItemsInfinite(
    {
      parentType,
      parentID,
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !parentID || !merchantID,
    },
  )

  // Use query items when partial menu is enabled, otherwise use Redux items
  const items = useMemo(() => {
    if (enablePartialMenu && itemsData && parentID) {
      const allItems = flattenInfiniteItems(itemsData) || []
      return mapItemsV2ToItems(allItems)
    }
    return reduxItems
  }, [enablePartialMenu, itemsData, parentID, reduxItems])

  const textMap = useTranslationsMap({
    sortItemsSuccess: "menu.notifications.sortItems.successMessage",
  })

  useEffect(() => {
    setSortedIndexes([])
  }, [items])

  const onSortEnd = (sortedItems: Item[]) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewRearrangeItem,
      action: EventAction.submitRearrangeItem,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: !isMartDepartmentStructure ? categoryID : departmentID,
        IS_PARTIAL_MENU: enablePartialMenu,
      },
    })

    const sorts = isUCS
      ? sortMenuItemNewLogicWithSortedIndexes({
          originalList: items,
          sortedIndexes,
          sortKey: "sortOrder",
          itemKey: "itemID",
        })
      : sortedItems.map((item, index) => ({
          resourceID: item.itemID ?? "",
          sortOrder: sortedItems.length - index,
        }))

    dispatch(
      !isMartDepartmentStructure
        ? actionCreator.menu.sortItems({
            merchantID,
            categoryID,
            sorts,
            successMessage: textMap.sortItemsSuccess,
            merchantGroupID,
            menuGroupID,
          })
        : actionCreator.menu.sortDepartmentItems({
            merchantID,
            departmentID,
            sorts,
            successMessage: textMap.sortItemsSuccess,
            merchantGroupID,
            menuGroupID,
          }),
    )
  }

  return (
    <SortDrawer
      title={useTranslation("menu.drawers.sort.items")}
      items={items}
      visible={visible}
      onClose={() => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeItem,
          action: EventAction.clickExit,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            CATEGORY_ID: !isMartDepartmentStructure ? categoryID : departmentID,
            IS_PARTIAL_MENU: enablePartialMenu,
          },
        })
        onClose()
      }}
      onSave={onSortEnd}
      itemSize={rowHeight}
      renderRowContent={(item: Item) => <MenuItemRow item={item} />}
      onSort={({ oldIndex, newIndex }) => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeItem,
          action: EventAction.rearrangeItem,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            CATEGORY_ID: !isMartDepartmentStructure ? categoryID : departmentID,
            IS_PARTIAL_MENU: enablePartialMenu,
          },
        })
        if (isUCS) {
          setSortedIndexes(prevState => [...prevState, [oldIndex, newIndex]])
        }
      }}
      onClear={() => {
        setSortedIndexes([])
      }}
      data-testid="sort-drawer"
    />
  )
}
